import type { MessageDescriptor } from '@lingui/core';
import { AsyncThunkAction } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { toast, ToastOptions } from 'react-toastify';
import { describeApiError } from 'shared/errors';
import { useAppDispatch } from './hooks';

/**
 * Текст тоста об успехе: либо готовая строка, либо функция от результата.
 * Функция может вернуть `undefined` — тогда тост не показывается.
 */
type ToastMessage<T> = string | ((value: T) => string | undefined);

export interface DispatchToastMessages<Returned> {
  success?: ToastMessage<Returned>;
  /**
   * Заголовок (`` msg`Не удалось …` ``), к которому хелпер сам добавит причину отказа из реестра
   * кодов (`shared/errors`). Готовой строки тип не принимает: из неё причину не достать.
   */
  error?: MessageDescriptor;
  successOptions?: ToastOptions;
  errorOptions?: ToastOptions;
}

function resolveMessage<T>(message: ToastMessage<T> | undefined, value: T): string | undefined {
  if (message === undefined) return undefined;
  return typeof message === 'function' ? message(value) : message;
}

/**
 * Диспатчит async thunk и показывает тост по его результату.
 *
 * Уведомления — часть UI, поэтому reducer'ы и thunk'и о них не знают: текст задаёт то место,
 * которое вызывает действие, и один и тот же thunk может сообщать о себе по-разному на разных
 * экранах.
 *
 * Ошибка **не пробрасывается** дальше: она уже показана пользователю. Вместо этого возвращается
 * `undefined` — большинство мест вызова живут в `useEffect` без `catch`, и проброс превратился бы
 * там в unhandled rejection. Если результат нужен, проверяйте его на `undefined`.
 *
 * @example
 * const dispatchWithToast = useDispatchWithToast();
 *
 * // загрузка: сообщаем только о провале — заголовок и причина отказа бэкенда
 * dispatchWithToast(fetchLocation({ id }), { error: msg`Не удалось загрузить локацию` });
 *
 * // сохранение: нужен результат
 * const location = await dispatchWithToast(upsertLocation(data), {
 *   success: t`Локация успешно сохранена`,
 *   error: msg`Не удалось сохранить локацию`,
 * });
 * if (!location) return;
 * navigate(Pages.LOCATIONS.builders.edit(location.id));
 */
export function useDispatchWithToast() {
  const dispatch = useAppDispatch();

  return useCallback(
    async <Returned>(
      action: AsyncThunkAction<Returned, any, any>,
      messages: DispatchToastMessages<Returned> = {},
    ): Promise<Returned | undefined> => {
      try {
        const result = await dispatch(action).unwrap();

        const successMessage = resolveMessage(messages.success, result);
        if (successMessage) {
          toast.success(successMessage, messages.successOptions);
        }

        return result;
      } catch (error) {
        if (messages.error) {
          toast.error(describeApiError(error, messages.error), messages.errorOptions);
        }

        return undefined;
      }
    },
    [dispatch],
  );
}
